import { useEffect, useRef, useState } from "react"
import axios from "axios"

/**
 * Tracks whether any XHR is in flight, app-wide.
 *
 * Changing supplier or retailer fires a dozen independent fetches across
 * panels that each own their own loading flag. Until they resolve the page
 * sits on the previous supplier's numbers with nothing to say it is working —
 * it reads as a freeze, and you can't tell stale data from current data.
 * A single global signal fixes that everywhere at once, including the pages
 * whose panels have no loading state of their own.
 *
 * Two details that matter:
 *
 *  - The count lives in a ref and only flips React state when the *boolean*
 *    changes. Storing the count in state would re-render the whole shell twice
 *    per request — around 30 renders on a supplier switch, which would add to
 *    the very jank this is meant to report.
 *
 *  - `delay` avoids a strobe on fast calls; `minVisible` stops the bar
 *    vanishing before it has been seen.
 */
export default function useRequestActivity({ delay = 120, minVisible = 400 } = {}) {
  const [busy, setBusy] = useState(false)
  const countRef = useRef(0)
  const showTimer = useRef(null)
  const hideTimer = useRef(null)
  const shownAt = useRef(0)

  useEffect(() => {
    const clearTimers = () => {
      clearTimeout(showTimer.current)
      clearTimeout(hideTimer.current)
      showTimer.current = null
      hideTimer.current = null
    }

    const sync = () => {
      const active = countRef.current > 0

      if (active) {
        clearTimeout(hideTimer.current)
        hideTimer.current = null
        if (showTimer.current) return
        showTimer.current = setTimeout(() => {
          showTimer.current = null
          shownAt.current = Date.now()
          setBusy(true)
        }, delay)
        return
      }

      // Idle: cancel any pending show, then schedule the hide. The hide must be
      // scheduled even when a pending show was cancelled — the bar can still be
      // visible from an earlier request, and returning early here left it
      // sweeping forever (stuck-bar bug, fixed 2026-08-23).
      if (showTimer.current) {
        clearTimeout(showTimer.current)
        showTimer.current = null
      }
      if (hideTimer.current) return
      const held = Date.now() - shownAt.current
      hideTimer.current = setTimeout(() => {
        hideTimer.current = null
        setBusy(false)
      }, Math.max(0, minVisible - held))
    }

    const onStart = () => {
      countRef.current += 1
      sync()
    }
    const onEnd = () => {
      countRef.current = Math.max(0, countRef.current - 1)
      sync()
    }

    const reqId = axios.interceptors.request.use(
      (config) => {
        onStart()
        return config
      },
      (error) => {
        onEnd()
        return Promise.reject(error)
      },
    )
    const resId = axios.interceptors.response.use(
      (response) => {
        onEnd()
        return response
      },
      // Aborted requests land here too, so the count can't leak.
      (error) => {
        onEnd()
        return Promise.reject(error)
      },
    )

    return () => {
      axios.interceptors.request.eject(reqId)
      axios.interceptors.response.eject(resId)
      clearTimers()
      countRef.current = 0
    }
  }, [delay, minVisible])

  return busy
}
